/* eslint-disable max-lines, no-param-reassign */
import { mergeStyles, IStyleConfig } from '@cian/utils';
import * as React from 'react';

import * as styles from './dropdown.css';
import { getKeyboardEventCode, OutsideClick } from '@cian/components';

import { debounce } from '../../../utils/functional/debounce';
import { startsWith } from '../../../utils/functional/starts_with';
import { getKeyPressed } from '../../../utils/events';

export type DropdownValue = string | number;

export interface IDropdownValue {
  value: DropdownValue;
  label: string;
}

export interface IAbstractDropdownProps {
  value: DropdownValue | DropdownValue[];
  onValueChange: (value: DropdownValue | DropdownValue[]) => void;
  values: IDropdownValue[];
  multiple?: boolean;
  required?: boolean;
  label?: string;
  placeholder?: string;
  invalid?: boolean;
  buttonStyle?: IStyleConfig;
  containerStyle?: IStyleConfig;
  placeholderStyle?: IStyleConfig;
  placeholderOpenedStyle?: IStyleConfig;
  popupStyle?: IStyleConfig;
  popupContentStyle?: IStyleConfig;
  valueStyle?: IStyleConfig;
  valueActiveStyle?: IStyleConfig;
  valueSelectedStyle?: IStyleConfig;
}

export interface IAbstractDropdownState {
  opened: boolean;
  selectedIndex: number | null;
}

export interface IDropdownButton {
  focus(): void;
  blur(): void;
}

export abstract class AbstractDropdown<
  P extends IAbstractDropdownProps,
  S extends IAbstractDropdownState,
  B extends IDropdownButton
> extends React.Component<P, S> {
  public static defaultProps = {
    multiple: false,
  };

  protected button: B | null = null;
  protected popupContent: {
    scrollTo: (index: number) => void;
  } | null = null;

  private searchTerm: string;
  private clearTerm: () => void;

  public state = {
    opened: false,
    selectedIndex: null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any;

  public constructor(props: P) {
    super(props);

    this.searchTerm = '';
    this.clearTerm = debounce(() => {
      this.searchTerm = '';
    }, 450);
  }

  public render() {
    const { activeValue, selectedLabel, selectedValue } = this.calculateValues();

    return (
      <OutsideClick
        onOutsideClick={() => this.close()}
        rootElement={
          <span
            {...mergeStyles(
              styles['container'],
              this.state.opened && styles['opened'],
              this.props.required && styles['required'],
              this.props.containerStyle,
            )}
          />
        }
      >
        {this.renderButton(selectedLabel)}
        {this.state.opened &&
          this.renderPopup({
            activeValue,
            selectedValue,
          })}
      </OutsideClick>
    );
  }

  public abstract renderButton(selectedLabel: string | JSX.Element): JSX.Element;

  public abstract renderPopup(values: { activeValue: IDropdownValue[]; selectedValue: IDropdownValue[] }): JSX.Element;

  public focus() {
    if (this.button) {
      this.button.focus();
    }
    this.open();
  }

  public blur() {
    if (this.button) {
      this.button.blur();
    }
    this.close();
  }

  public toggleOpened() {
    if (this.state.opened) {
      this.close();
    } else {
      this.open();
    }
  }

  public handleValueMouseOver(value: IDropdownValue) {
    const index = this.props.values.indexOf(value);
    const selectedIndex = index >= 0 ? index : null;

    this.setState({ selectedIndex } as S);
  }

  public handleKeyDown(event: React.KeyboardEvent<EventTarget>) {
    const nativeEvent = event.nativeEvent as KeyboardEvent;
    const code = getKeyboardEventCode(nativeEvent);

    if (code === 'Escape') {
      this.close();

      return;
    }

    if (this.props.multiple) {
      // disable keyboard iteraction while selectedValue is not implemented in PopupContent with multiple values
      return;
    }

    switch (code) {
      case 'ArrowDown':
      case 'Down':
        event.preventDefault();
        this.incrementSelectedIndex();
        break;

      case 'ArrowUp':
      case 'Up':
        event.preventDefault();
        this.decrementSelectedIndex();
        break;

      case 'NumpadEnter':
      case 'Enter':
        event.preventDefault();
        if (typeof this.state.selectedIndex === 'number') {
          this.handleValueClick(this.props.values[this.state.selectedIndex]);
        }
        break;

      default:
        const letter = getKeyPressed(nativeEvent);
        this.clearTerm();
        if (typeof letter !== 'undefined') {
          this.searchTerm += letter;
        }

        const index = this.findValueIndex(this.searchTerm);

        if (index >= 0) {
          this.setSelectedIndex(index);
        }

        break;
    }
  }

  public handleValueClick(value: IDropdownValue) {
    if (this.props.multiple) {
      const changedValue = this.props.value as DropdownValue[];
      const valueIndex = changedValue.indexOf(value.value);

      if (valueIndex >= 0) {
        changedValue.splice(valueIndex, 1);
      } else {
        changedValue.push(value.value);
      }

      this.props.onValueChange(changedValue);
    } else {
      this.props.onValueChange(value.value);
      this.close();
    }
  }

  public close() {
    if (this.button) {
      this.button.blur();
    }

    this.setState({
      opened: false,
      selectedIndex: null,
    } as S);
  }

  private open() {
    if (this.button) {
      this.button.focus();
    }

    const selectedValue = this.props.values.filter(value => value.value === this.props.value).shift();

    const selectedIndex = this.props.values.indexOf(selectedValue as IDropdownValue);

    this.setState({
      opened: true,
      selectedIndex: selectedIndex >= 0 ? selectedIndex : null,
    } as S);
  }

  private calculateValues(): {
    activeValue: IDropdownValue[];
    selectedValue: IDropdownValue[];
    selectedLabel: string | JSX.Element;
  } {
    const convertedValue = Array.isArray(this.props.value) ? this.props.value : [this.props.value];
    const activeValue = this.props.values.filter(
      value => (convertedValue as DropdownValue[]).indexOf(value.value) >= 0,
    );

    const selectedLabel =
      activeValue.length > 0 ? (
        activeValue.map(item => item.label).join(', ')
      ) : (
        <span
          {...mergeStyles(
            styles[this.state.opened ? 'placeholder-opened' : 'placeholder'],
            this.state.opened ? this.props.placeholderOpenedStyle : this.props.placeholderStyle,
          )}
        >
          {this.props.placeholder}
        </span>
      );

    let selectedValue: IDropdownValue[] = [];

    if (this.props.multiple) {
      selectedValue = activeValue;
    } else if (typeof this.state.selectedIndex === 'number') {
      selectedValue = [this.props.values[this.state.selectedIndex]];
    }

    return {
      activeValue,
      selectedLabel,
      selectedValue,
    };
  }

  private incrementSelectedIndex() {
    let selectedIndex = typeof this.state.selectedIndex !== 'number' ? 0 : this.state.selectedIndex + 1;

    this.setSelectedIndex(selectedIndex);
  }

  private decrementSelectedIndex() {
    let selectedIndex =
      typeof this.state.selectedIndex !== 'number' ? this.props.values.length - 1 : this.state.selectedIndex - 1;

    this.setSelectedIndex(selectedIndex);
  }

  private setSelectedIndex(selectedIndex: number) {
    if (selectedIndex < 0) {
      selectedIndex = 0;
    }

    if (selectedIndex >= this.props.values.length) {
      selectedIndex = this.props.values.length - 1;
    }

    this.setState({
      selectedIndex,
    } as S);

    if (this.popupContent && this.popupContent.scrollTo) {
      this.popupContent.scrollTo(selectedIndex);
    }
  }

  private findValueIndex(startingLetters: string) {
    if (startingLetters === '') {
      return -1;
    }

    let index = -1;

    this.props.values.some((value: IDropdownValue, i: number) => {
      if (startsWith(value.label.toLowerCase(), startingLetters.toLowerCase())) {
        index = i;

        return true;
      }

      return false;
    });

    return index;
  }
}
